// A5 prescription for printing (FR-RX-6). Burmese instructions come from the
// dosage phrase list so the patient reads them in their own script.
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui';
import { useDosagePhrases } from './pickers';
import type { RxItemRow, DiagnosisRow } from './repo';
import type { PatientIndexRow } from '@/lib/db/schema';
import { ageLabel } from '@/features/patients/repo';
import { formatDate, toMyanmarDigits } from '@/lib/myanmar/digits';

export function PrescriptionPrint({ patient, items, diagnoses, doctorName, followUp, clinicName, clinicPhone, autoPrint = false, onClose }: {
  patient: PatientIndexRow; items: RxItemRow[]; diagnoses: DiagnosisRow[]; doctorName: string | null; followUp: string;
  clinicName: string; clinicPhone: string | null; autoPrint?: boolean; onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const my = i18n.language === 'my';
  const freqs = useDosagePhrases('frequency');
  const instructions = useDosagePhrases('instruction');
  const units = useDosagePhrases('dose_unit');

  useEffect(() => {
    if (!autoPrint) return;
    const id = setTimeout(() => window.print(), 200);
    return () => clearTimeout(id);
  }, [autoPrint]);

  const phrase = (list: { code: string; text_en: string; text_my: string }[], code: string | null | undefined) => {
    const p = list.find((x) => x.code === code);
    return p ? (my ? p.text_my : p.text_en) : (code ?? '');
  };
  const age = ageLabel(patient);
  const d = new Date();
  const num = (x: number | string) => (my ? toMyanmarDigits(x) : String(x));

  return (
    <div className="grid gap-4">
      <div className="print-rx mx-auto w-full bg-white p-6 text-ink" style={{ maxWidth: '148mm' }}>
        <div className="border-b border-ink/20 pb-2 text-center">
          <div className="text-lg font-bold">{clinicName}</div>
          {clinicPhone && <div className="text-xs">{clinicPhone}</div>}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <div><span className="text-ink/60">{t('patient.number')}: </span>{patient.patient_no}</div>
          <div className="text-right">{formatDate(d, my ? 'my' : 'en')}</div>
          <div className="col-span-2 text-base font-bold">
            {patient.honorific && patient.honorific !== 'none' ? t(`honorific.${patient.honorific}`) + ' ' : ''}
            {my ? (patient.name_my ?? patient.name_en) : (patient.name_en ?? patient.name_my)}
            {age && <span className="ml-2 text-sm font-normal">{num(age.years)} {t('patient.ageYears')}</span>}
          </div>
          {diagnoses.length > 0 && (
            <div className="col-span-2 text-sm"><span className="text-ink/60">{t('consult.diagnosis')}: </span>
              {diagnoses.map((x) => (my ? (x.title_my ?? x.label ?? x.title_en) : (x.title_en ?? x.label))).filter(Boolean).join(', ')}
            </div>
          )}
        </div>

        <div className="mt-4 text-2xl font-bold">℞</div>
        <ol className="mt-1 grid gap-2.5">
          {items.map((it, i) => (
            <li key={it.id} className="grid grid-cols-[1.5rem_1fr] gap-1 border-b border-dashed border-ink/15 pb-2">
              <span className="font-bold">{num(i + 1)}.</span>
              <div>
                <div className="font-semibold">{it.drug_name}</div>
                <div className="text-sm">
                  {it.dose_qty ? `${num(it.dose_qty)} ${phrase(units, it.dose_unit)} ` : ''}
                  {phrase(freqs, it.frequency_code)}
                  {it.duration_days ? ` · ${num(it.duration_days)} ${my ? 'ရက်' : 'days'}` : ''}
                  {(it.instruction_codes ?? []).length > 0 ? ` · ${(it.instruction_codes ?? []).map((c) => phrase(instructions, c)).join(', ')}` : ''}
                </div>
                {it.free_text && <div className="text-sm italic">{it.free_text}</div>}
                <div className="text-xs text-ink/60">{t('consult.quantity')}: {num(it.quantity)}{it.buy_outside ? ` · ${t('consult.buyOutside')}` : ''}</div>
              </div>
            </li>
          ))}
        </ol>

        {followUp && <div className="mt-3 text-sm"><span className="text-ink/60">{t('consult.followUp')}: </span><b>{formatDate(followUp, my ? 'my' : 'en')}</b></div>}
        <div className="mt-10 text-right text-sm">
          <div className="inline-block border-t border-ink/40 pt-1">{doctorName}</div>
        </div>
      </div>
      <div className="no-print flex justify-end gap-2">
        <Button onClick={onClose}>{t('common.close')}</Button>
        <Button variant="primary" onClick={() => window.print()}>{t('common.print')}</Button>
      </div>
    </div>
  );
}
